'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import SafeImage from './SafeImage'
import { heroSlides } from '@/data/homepage'
import { SITE } from '@/lib/site'
import './HeroSection.css'

const titleClass =
  'text-2xl md:text-3xl lg:text-[2.35rem] font-bold leading-tight'
const subtitleClass = 'text-base md:text-xl font-medium text-white/90 mt-2.5'
const descClass = 'text-sm md:text-base text-white/80 leading-relaxed mt-3 max-w-md'

export default function HeroSlider() {
  const [active, setActive] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [])

  const go = (index: number) => {
    setActive((index + heroSlides.length) % heroSlides.length)
  }

  const slide = heroSlides[active]
  const isFirst = active === 0

  return (
    <section className="hero-section relative w-full min-h-[520px] md:min-h-[580px] lg:min-h-[640px] overflow-hidden pb-8 md:pb-0">
      {heroSlides.map((item, index) => (
        <div
          key={item.title}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={index !== active}
        >
          {index === active || index === (active + 1) % heroSlides.length ? (
            <SafeImage
              src={item.image}
              alt={item.imageAlt}
              fill
              priority={index === 0}
              sizes="100vw"
              className="object-cover"
            />
          ) : null}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(-45deg, #052a8dff 0%, #002855 100%)',
              opacity: 0.9,
            }}
          />
        </div>
      ))}

      <div className="absolute inset-0 flex items-center py-8 md:py-10 z-[1]">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-center">
            <div className="hero-circle relative mx-auto md:mx-0 w-[200px] h-[200px] sm:w-[240px] sm:h-[240px] md:w-[340px] md:h-[340px] lg:w-[400px] lg:h-[400px]">
              <div className="absolute inset-0 rounded-full border border-white/15" />
              <div className="absolute inset-3 md:inset-4 rounded-full border-[3px] border-[#feb400] opacity-90 animate-ping-yellow" />
              <div className="absolute inset-7 md:inset-9 rounded-full border-[3px] border-white opacity-85 animate-ping-white" />
              <div className="absolute inset-9 md:inset-11 rounded-full overflow-hidden bg-[#020c34] shadow-2xl shadow-black/40">
                {heroSlides.map((item, index) =>
                  index === active || index === (active + 1) % heroSlides.length ? (
                    <div
                      key={`circle-${item.title}`}
                      className={`absolute inset-0 transition-opacity duration-1000 ${
                        index === active ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <SafeImage
                        src={item.circleImage}
                        alt={item.circleImageAlt}
                        fill
                        sizes="(max-width: 768px) 240px, 400px"
                        className="object-cover"
                        priority={index === 0}
                      />
                    </div>
                  ) : null
                )}
              </div>
            </div>

            <div className="text-white max-w-xl md:ml-2 lg:ml-4">
              {/* Tek H1 her zaman DOM'da ilk baslik olarak kalir */}
              <div className={isFirst ? undefined : 'sr-only'}>
                <div className={`${titleClass} whitespace-nowrap`}>
                  <span className="text-[#feb400]">Doku Akustik</span>{' '}
                  <h1 className="inline text-inherit font-bold">Akustik Panel</h1>
                </div>
                <p className={subtitleClass}>Profesyonel Çözümler & Estetik Tasarım</p>
                <p className={descClass}>{heroSlides[0].description}</p>
              </div>

              {!isFirst && (
                <div>
                  <p className={titleClass}>{slide.title}</p>
                  {slide.subtitle ? (
                    <p className={subtitleClass}>{slide.subtitle}</p>
                  ) : null}
                  <p className={descClass}>{slide.description}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                <Link
                  href="/akustik-paneller/"
                  className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-md text-sm transition-colors min-h-[44px]"
                  style={{
                    background: '#feb400',
                    borderColor: '#feb400',
                    color: '#1a1a1a',
                    borderWidth: 1,
                  }}
                >
                  HİZMETİ GÖRÜNTÜLE
                </Link>
                <a
                  href={SITE.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3 font-semibold rounded-md text-sm hover:bg-white hover:text-[#232f3e] min-h-[44px]"
                >
                  TEKLİF AL
                  <ArrowRight size={16} />
                </a>
              </div>

              <div className="flex md:hidden justify-center gap-1 mt-4">
                {heroSlides.map((item, index) => (
                  <button
                    key={`m-${item.title}`}
                    type="button"
                    aria-label={`Slide ${index + 1}`}
                    onClick={() => setActive(index)}
                    className="min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <span
                      className={`block w-3 h-3 rounded-full ${
                        index === active ? 'bg-[#feb400]' : 'bg-white/70'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Önceki"
        onClick={() => go(active - 1)}
        className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/25 hover:bg-[#c2410c] rounded-full text-white items-center justify-center z-10"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        type="button"
        aria-label="Sonraki"
        onClick={() => go(active + 1)}
        className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/25 hover:bg-[#c2410c] rounded-full text-white items-center justify-center z-10"
      >
        <ChevronRight size={22} />
      </button>

      <div className="hidden md:flex absolute bottom-5 left-1/2 -translate-x-1/2 gap-1 z-10">
        {heroSlides.map((item, index) => (
          <button
            key={item.title}
            type="button"
            aria-label={`Slide ${index + 1}`}
            onClick={() => setActive(index)}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <span
              className={`block w-3 h-3 rounded-full ${
                index === active ? 'bg-[#feb400]' : 'bg-white/70 hover:bg-white'
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  )
}
